import { useState, useEffect, useRef, useCallback } from 'react';

// Performance metrics storage
const performanceMetrics = {
  pageLoads: [],
  apiCalls: [],
  cacheHits: 0,
  cacheMisses: 0,
  errors: []
};

// Custom hook for performance monitoring
export const usePerformanceMonitoring = () => {
  const [metrics, setMetrics] = useState({
    averageLoadTime: 0,
    cacheHitRate: 0,
    errorRate: 0,
    totalRequests: 0
  });

  const startTimer = useRef(null);

  // Start timing an operation
  const startTiming = useCallback((operationType = 'general') => {
    startTimer.current = {
      start: performance.now(),
      type: operationType
    };
    return startTimer.current.start;
  }, []);

  // End timing and record metrics
  const endTiming = useCallback((fromCache = false, itemCount = 0, error = null) => {
    if (!startTimer.current) return 0;

    const endTime = performance.now();
    const duration = endTime - startTimer.current.start;
    const operationType = startTimer.current.type;

    // Record the API call
    performanceMetrics.apiCalls.push({
      type: operationType,
      duration,
      fromCache,
      itemCount,
      timestamp: Date.now(),
      error: !!error
    });

    // Update cache metrics
    if (fromCache) {
      performanceMetrics.cacheHits++;
    } else {
      performanceMetrics.cacheMisses++;
    }

    // Record errors
    if (error) {
      performanceMetrics.errors.push({
        type: operationType,
        error: error.message || error,
        timestamp: Date.now()
      });
    }

    // Clean old metrics (keep last 100)
    if (performanceMetrics.apiCalls.length > 100) {
      performanceMetrics.apiCalls = performanceMetrics.apiCalls.slice(-100);
    }
    if (performanceMetrics.errors.length > 50) {
      performanceMetrics.errors = performanceMetrics.errors.slice(-50);
    }

    startTimer.current = null;
    return duration;
  }, []);

  // Record page load time
  const recordPageLoad = useCallback((pageName, loadTime) => {
    performanceMetrics.pageLoads.push({
      page: pageName,
      loadTime,
      timestamp: Date.now()
    });

    // Keep last 50 page loads
    if (performanceMetrics.pageLoads.length > 50) {
      performanceMetrics.pageLoads = performanceMetrics.pageLoads.slice(-50);
    }
  }, []);

  // Calculate metrics
  const calculateMetrics = useCallback(() => {
    const recentCalls = performanceMetrics.apiCalls.slice(-50); // Last 50 calls
    const totalCacheOperations = performanceMetrics.cacheHits + performanceMetrics.cacheMisses;
    
    const averageLoadTime = recentCalls.length > 0 
      ? recentCalls.reduce((sum, call) => sum + call.duration, 0) / recentCalls.length
      : 0;

    const cacheHitRate = totalCacheOperations > 0 
      ? (performanceMetrics.cacheHits / totalCacheOperations) * 100
      : 0;

    const errorRate = recentCalls.length > 0 
      ? (recentCalls.filter(call => call.error).length / recentCalls.length) * 100
      : 0;

    return {
      averageLoadTime: Math.round(averageLoadTime),
      cacheHitRate: Math.round(cacheHitRate * 100) / 100,
      errorRate: Math.round(errorRate * 100) / 100,
      totalRequests: performanceMetrics.apiCalls.length
    };
  }, []);

  // Update metrics periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics(calculateMetrics());
    }, 5000); // Update every 5 seconds

    // Initial calculation
    setMetrics(calculateMetrics());

    return () => clearInterval(interval);
  }, [calculateMetrics]);

  // Get detailed performance data
  const getPerformanceData = useCallback(() => ({
    metrics: calculateMetrics(),
    recentCalls: performanceMetrics.apiCalls.slice(-20),
    recentErrors: performanceMetrics.errors.slice(-10),
    recentPageLoads: performanceMetrics.pageLoads.slice(-10)
  }), [calculateMetrics]);

  // Clear performance data
  const clearMetrics = useCallback(() => {
    performanceMetrics.pageLoads = [];
    performanceMetrics.apiCalls = [];
    performanceMetrics.cacheHits = 0;
    performanceMetrics.cacheMisses = 0;
    performanceMetrics.errors = [];
    setMetrics({
      averageLoadTime: 0,
      cacheHitRate: 0,
      errorRate: 0,
      totalRequests: 0
    });
  }, []);

  return {
    metrics,
    startTiming,
    endTiming,
    recordPageLoad,
    getPerformanceData,
    clearMetrics
  };
};

// Hook for measuring component render performance
export const useRenderPerformance = (componentName) => {
  const renderCount = useRef(0);
  const lastRenderTime = useRef(performance.now());

  useEffect(() => {
    renderCount.current++;
    const now = performance.now();
    const renderTime = now - lastRenderTime.current;
    lastRenderTime.current = now;

    // Log slow renders in development
    if (process.env.NODE_ENV === 'development' && renderTime > 16) {
      // Slow render detected
    }
  });

  return {
    renderCount: renderCount.current,
    componentName
  };
};

// Hook for tracking user interactions
export const useInteractionTracking = () => {
  const trackInteraction = useCallback((action, target, duration = 0) => {
    if (process.env.NODE_ENV === 'development') {
      // Development mode interaction tracking
    }

    // Store interaction for analytics
    performanceMetrics.apiCalls.push({
      type: 'user_interaction',
      duration,
      action,
      target,
      timestamp: Date.now(),
      fromCache: false,
      itemCount: 1,
      error: false
    });
  }, []);

  return { trackInteraction };
};

export default usePerformanceMonitoring;
